import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'

export interface KsTemplate {
  id: string
  name: string
  variables?: string[]
}

export interface KsSender {
  id: string
  label?: string
  company_name?: string
  first_name?: string
  last_name?: string
}

export interface KsBalance {
  shipment_credits: number
}

export interface UseKundenschreiberSetupResult {
  templates: KsTemplate[] | undefined
  senders: KsSender[] | undefined
  balance: KsBalance | null | undefined
  ksDebug: unknown
  ksTemplatesError: unknown
  ksTemplateErrorMsg: string

  selectedTemplateId: string
  setSelectedTemplateId: (id: string) => void
  selectedSenderId: string
  setSelectedSenderId: (id: string) => void

  defaultsApplied: boolean
  defaults: { defaultTemplateId: string | null; defaultSenderId: string | null } | undefined
  defaultsChanged: boolean
  saveDefaults: () => Promise<void>
  savingDefaults: boolean
}

// Tenant-wide Kundenschreiber template / sender / balance state.
// Extracted from LetterReviewCard so the daily carousel and the
// per-prospect letter modal share identical selection behaviour.
export function useKundenschreiberSetup(): UseKundenschreiberSetupResult {
  const queryClient = useQueryClient()
  const [ksTemplateErrorMsg, setKsTemplateErrorMsg] = useState('')

  const { data: templates, error: ksTemplatesError } = useQuery({
    queryKey: ['kundenschreiber-templates'],
    queryFn: async () => {
      try {
        const r = await apiClient.get('/shipping/kundenschreiber/templates')
        setKsTemplateErrorMsg('')
        return (r.data?.data ?? []) as KsTemplate[]
      } catch (err: unknown) {
        const axErr = err as { response?: { data?: { message?: string; error?: string }; status?: number }; message?: string }
        const msg =
          axErr?.response?.data?.message ||
          axErr?.response?.data?.error ||
          axErr?.message ||
          'Unbekannter Fehler'
        setKsTemplateErrorMsg(`${axErr?.response?.status || '?'}: ${msg}`)
        throw err
      }
    },
    staleTime: 5 * 60_000,
    retry: 2,
    retryDelay: 3000,
  })

  const { data: senders } = useQuery({
    queryKey: ['kundenschreiber-senders'],
    queryFn: async () => {
      const r = await apiClient.get('/shipping/kundenschreiber/senders')
      return (r.data?.data ?? []) as KsSender[]
    },
    staleTime: 5 * 60_000,
    retry: false,
  })

  const { data: balance } = useQuery({
    queryKey: ['kundenschreiber-balance'],
    queryFn: () =>
      apiClient
        .get('/shipping/kundenschreiber/balance')
        .then((r) => r.data as KsBalance)
        .catch(() => null),
    staleTime: 60_000,
  })

  const { data: ksDebug } = useQuery({
    queryKey: ['kundenschreiber-debug'],
    queryFn: () =>
      apiClient
        .get('/shipping/kundenschreiber/debug')
        .then((r) => r.data)
        .catch((err) => ({
          fetchError:
            err?.response?.data?.message || err?.response?.data || err?.message || String(err),
          status: err?.response?.status,
        })),
    staleTime: 60_000,
  })

  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [selectedSenderId, setSelectedSenderId] = useState('')
  const [defaultsApplied, setDefaultsApplied] = useState(false)

  const { data: defaults } = useQuery({
    queryKey: ['kundenschreiber-defaults'],
    queryFn: async () => {
      const r = await apiClient.get('/shipping/kundenschreiber/defaults')
      return r.data as { defaultTemplateId: string | null; defaultSenderId: string | null }
    },
    staleTime: 5 * 60_000,
    retry: false,
  })

  useEffect(() => {
    if (defaultsApplied || !defaults) return
    if (!Array.isArray(templates) || !Array.isArray(senders)) return

    if (defaults.defaultTemplateId && !selectedTemplateId) {
      const exists = templates.some((t) => t.id === defaults.defaultTemplateId)
      if (exists) setSelectedTemplateId(defaults.defaultTemplateId)
    }
    if (defaults.defaultSenderId && !selectedSenderId) {
      const exists = senders.some((s) => s.id === defaults.defaultSenderId)
      if (exists) setSelectedSenderId(defaults.defaultSenderId)
    }
    setDefaultsApplied(true)
  }, [defaults, templates, senders, defaultsApplied, selectedTemplateId, selectedSenderId])

  const defaultsChanged = Boolean(
    defaultsApplied &&
      defaults &&
      ((selectedTemplateId || null) !== (defaults.defaultTemplateId || null) ||
        (selectedSenderId || null) !== (defaults.defaultSenderId || null)),
  )

  const [savingDefaults, setSavingDefaults] = useState(false)
  const saveDefaults = async () => {
    setSavingDefaults(true)
    try {
      await apiClient.put('/shipping/kundenschreiber/defaults', {
        defaultTemplateId: selectedTemplateId || null,
        defaultSenderId: selectedSenderId || null,
      })
      queryClient.invalidateQueries({ queryKey: ['kundenschreiber-defaults'] })
    } catch {
      // Best-effort: UI bleibt im aktuellen Zustand; harter Fehler ist selten
      // (liest/schreibt nur Tenant-Settings).
    }
    setSavingDefaults(false)
  }

  return {
    templates,
    senders,
    balance,
    ksDebug,
    ksTemplatesError,
    ksTemplateErrorMsg,
    selectedTemplateId,
    setSelectedTemplateId,
    selectedSenderId,
    setSelectedSenderId,
    defaultsApplied,
    defaults,
    defaultsChanged,
    saveDefaults,
    savingDefaults,
  }
}
